import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { paletteForEditing, samePalette } from 'fym-shared/src/music24/kitPalette';
import { DEFAULT_RELEASE_KIT_LOOK, ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { useGetReleaseKitLookQuery, useUpdateKitLookMutation } from '../../../../../redux/api/fymAPI';
import { SerializedKit } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { themeMessages } from './themeMessages';

type LookPatch = Partial<ReleaseKitLook>;

const NO_PALETTE: string[] = [];

interface UseKitLookDraftProps {
  accountId: number;
  catalogItemUuid: string;
  kit: SerializedKit | null;
}

export interface KitLookDraftState {
  /** The look of the open panel: the saved look with the draft on top. */
  look: ReleaseKitLook;
  /** The look that is saved. The closed panel shows it. */
  savedLook: ReleaseKitLook;
  /** The five colours of the open panel: the draft, else the saved colours of the song. Empty with no kit colours. */
  palette: string[];
  /** The five colours that are saved. The closed panel shows them. */
  savedPalette: string[];
  /** The five colours that a swatch edit starts from (the kit's, or the Music24 inks with no cover). */
  editablePalette: string[];
  /** The cover colours of the song (the auto colours). */
  coverPalette: string[];
  /** The colours of the open panel are the artist's own, not the cover colours. */
  hasPaletteOverride: boolean;
  /** The logo of the draft: a new upload shows before Save. */
  logoUrl: string | null;
  saveAsDefault: boolean;
  /** The draft is different from the saved look, or "Save as my default" is on. */
  isDirty: boolean;
  isSaving: boolean;
  /** The last Save failed. The draft stays. */
  hasError: boolean;
  /** Changes the draft only. No request goes. */
  change: (patch: LookPatch, logoUrl?: string | null) => void;
  toggleSaveAsDefault: () => void;
  /** Changes the colours of the draft only. Five `#rrggbb` colours. */
  changePalette: (palette: string[]) => void;
  /** Puts the cover colours back in the draft. */
  resetPalette: () => void;
  /** Sends one `PATCH .../look` with the changed keys. True when it saved. */
  save: () => Promise<boolean>;
  /** Drops the draft: the saved look comes back. */
  discard: () => void;
}

/** The keys of `draft` that hold a value different from `base`. */
export function lookDraftDiff(draft: LookPatch, base: ReleaseKitLook): LookPatch {
  const next: LookPatch = {};
  (Object.keys(draft) as Array<keyof ReleaseKitLook>).forEach((key) => {
    if (draft[key] !== undefined && draft[key] !== base[key]) {
      Object.assign(next, { [key]: draft[key] });
    }
  });
  return next;
}

/**
 * The look of one song as a draft (the song page, owner request 2026-10-06: "I should change the
 * theme and then click Save before it saves, or Cancel if I don't like the change"). A theme, a
 * font or a logo pick changes only the draft, and the preview shows it. `save` sends one
 * `PATCH .../look`; `discard` brings the saved look back. A failed save keeps the draft.
 *
 * After a save the draft stays on screen until the kit shows the new look: the PATCH makes the
 * page read the kit again, and while that read runs the query still gives the old look.
 */
export function useKitLookDraft({ accountId, catalogItemUuid, kit }: UseKitLookDraftProps): KitLookDraftState {
  const intl = useIntl();
  const { showSuccess, showError } = useShowToast();
  const savedDefault = useGetReleaseKitLookQuery({ accountId }, { skip: kit !== null });
  const [updateKitLook, { isLoading }] = useUpdateKitLookMutation();
  const [draft, setDraft] = useState<LookPatch>({});
  const [draftLogoUrl, setDraftLogoUrl] = useState<string | null | undefined>(undefined);
  // undefined: no change. null: the cover colours. A list: the artist's own colours.
  const [paletteDraft, setPaletteDraft] = useState<string[] | null | undefined>(undefined);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSettling, setIsSettling] = useState(false);

  const base = kit?.look ?? savedDefault.data?.look ?? DEFAULT_RELEASE_KIT_LOOK;
  const diff = useMemo(() => lookDraftDiff(draft, base), [base, draft]);
  const look: ReleaseKitLook = useMemo(() => ({ ...base, ...diff }), [base, diff]);
  const kitLogoUrl = kit?.logoUrl ?? null;
  const kitPalette = kit?.palette ?? NO_PALETTE;
  const coverPalette = kit?.coverPalette ?? NO_PALETTE;
  const savedOverride = kit?.paletteOverride ?? null;
  const hasPaletteChange = paletteDraft !== undefined && !samePalette(paletteDraft, savedOverride);
  const palette = useMemo(() => {
    if (paletteDraft === undefined) {
      return kitPalette;
    }
    return paletteDraft === null ? coverPalette : paletteDraft;
  }, [coverPalette, kitPalette, paletteDraft]);
  const editablePalette = useMemo(() => paletteForEditing(palette), [palette]);
  const hasPaletteOverride = paletteDraft === undefined ? savedOverride !== null : paletteDraft !== null;

  // A new saved look: a save that settles ends, and the draft keys that it holds now go.
  const shownBase = useRef({ base, savedOverride });
  useEffect(() => {
    if (shownBase.current.base === base && shownBase.current.savedOverride === savedOverride) {
      return;
    }
    shownBase.current = { base, savedOverride };
    if (isSettling) {
      setIsSettling(false);
      setDraft({});
      setDraftLogoUrl(undefined);
      setPaletteDraft(undefined);
      return;
    }
    setDraft((current) => lookDraftDiff(current, base));
    setPaletteDraft((current) => (current !== undefined && samePalette(current, savedOverride) ? undefined : current));
  }, [base, isSettling, savedOverride]);

  const change = useCallback((patch: LookPatch, logoUrl?: string | null) => {
    setIsSettling(false);
    setHasError(false);
    setDraft((current) => ({ ...current, ...patch }));
    if (logoUrl !== undefined) {
      setDraftLogoUrl(logoUrl);
    }
  }, []);

  const changePalette = useCallback((next: string[]) => {
    setIsSettling(false);
    setHasError(false);
    setPaletteDraft(next);
  }, []);

  const resetPalette = useCallback(() => {
    setIsSettling(false);
    setHasError(false);
    setPaletteDraft(null);
  }, []);

  const toggleSaveAsDefault = useCallback(() => {
    setHasError(false);
    setSaveAsDefault((current) => !current);
  }, []);

  const discard = useCallback(() => {
    setIsSettling(false);
    setHasError(false);
    setDraft({});
    setDraftLogoUrl(undefined);
    setPaletteDraft(undefined);
    setSaveAsDefault(false);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    setHasError(false);
    try {
      await updateKitLook({
        accountId,
        catalogItemUuid,
        ...diff,
        ...(hasPaletteChange ? { paletteOverride: paletteDraft } : {}),
        saveAsDefault,
      }).unwrap();
      if (saveAsDefault) {
        showSuccess(intl.formatMessage(themeMessages.saved));
      }
      setSaveAsDefault(false);
      if (Object.keys(diff).length > 0 || hasPaletteChange) {
        setIsSettling(true);
      } else {
        setDraft({});
        setDraftLogoUrl(undefined);
        setPaletteDraft(undefined);
      }
      return true;
    } catch (error: unknown) {
      console.error('Failed to save the theme of the song', error);
      showError(intl.formatMessage(themeMessages.saveFailed));
      setHasError(true);
      return false;
    }
  }, [
    accountId,
    catalogItemUuid,
    diff,
    hasPaletteChange,
    intl,
    paletteDraft,
    saveAsDefault,
    showError,
    showSuccess,
    updateKitLook,
  ]);

  const hasChanges = Object.keys(diff).length > 0 || hasPaletteChange || saveAsDefault;
  return {
    look,
    savedLook: isSettling ? look : base,
    palette,
    savedPalette: isSettling ? palette : kitPalette,
    editablePalette,
    coverPalette,
    hasPaletteOverride,
    logoUrl: draftLogoUrl !== undefined && diff.logoFileId !== undefined ? draftLogoUrl : kitLogoUrl,
    saveAsDefault,
    isDirty: !isSettling && hasChanges,
    isSaving: isLoading,
    hasError,
    change,
    toggleSaveAsDefault,
    changePalette,
    resetPalette,
    save,
    discard,
  };
}
